import { supabase } from './supabase'

/**
 * Uploads an image to Supabase storage
 * @param file The file to upload
 * @param bucket The storage bucket to upload to
 * @param userId The ID of the user uploading the file
 * @param fileName Optional custom file name
 * @returns Object containing the URL of the uploaded file or an error
 */
export async function uploadImage(
  file: File,
  bucket: string = 'public',
  userId?: string,
  fileName?: string
): Promise<{ url?: string; error?: string }> {
  try {
    // Generate a unique file name if not provided
    if (!fileName) {
      const fileExt = file.name.split('.').pop()
      fileName = `${userId || 'anonymous'}_${Date.now()}_${Math.random()
        .toString(36)
        .substring(2, 15)}.${fileExt}`
    }

    // Create the file path with a profile subdirectory for meetups
    const filePath = bucket === 'meetups' ? `profile/${fileName}` : fileName

    // Upload the file with user metadata
    const { data: _data, error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true,
        contentType: file.type,
        metadata: {
          userId: userId || 'anonymous',
          uploadedAt: new Date().toISOString(),
        },
      })

    if (uploadError) {
      console.error('Supabase upload error:', uploadError)
      throw new Error(`Error uploading image: ${uploadError.message}`)
    }

    // Get the public URL
    const { data: publicUrlData } = supabase.storage.from(bucket).getPublicUrl(filePath)

    return { url: publicUrlData.publicUrl }
  } catch (error) {
    console.error('Comprehensive upload error:', error)
    return {
      error: error instanceof Error ? error.message : 'Unknown error uploading image',
    }
  }
}

/**
 * Deletes an image from Supabase storage
 * @param url The full URL of the image to delete
 * @param userId Optional user ID for additional authorization
 * @returns Success status or error
 */
export async function deleteImage(
  url: string,
  _userId?: string
): Promise<{ success?: boolean; error?: string }> {
  try {
    // Extract the file path from the URL
    const urlObj = new URL(url)
    const pathSegments = urlObj.pathname.split('/')
    const bucket = pathSegments[1] // Get the bucket name
    const filePath = pathSegments.slice(2).join('/') // Get the file path

    // Delete the file
    const { error: deleteError } = await supabase.storage.from(bucket).remove([filePath])

    if (deleteError) {
      console.error('Supabase delete error:', deleteError)
      throw new Error(`Error deleting image: ${deleteError.message}`)
    }

    return { success: true }
  } catch (error) {
    console.error('Comprehensive delete error:', error)
    return {
      error: error instanceof Error ? error.message : 'Unknown error deleting image',
    }
  }
}
